import React, { useCallback, useEffect, useState } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Archive, Check, Copy, CreditCard, Plus, RefreshCw, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * Payment accounts: one Stripe connected account per partner, managed by an
 * administrator. A global dashboard view rather than an organisation tab, because an
 * account belongs to a partner and one account may serve several organisations.
 *
 * Nothing here is a secret: the row carries identifiers and the status Stripe last
 * reported. The onboarding link is the one thing to hand to the partner.
 */

export interface PaymentAccount {
  id: number
  displayName: string
  stripeAccountId: string
  livemode: boolean
  country: string
  defaultCurrency: string | null
  contactEmail: string | null
  chargesEnabled: boolean
  payoutsEnabled: boolean
  detailsSubmitted: boolean
  disabledReason: string | null
  isActive: boolean
  disconnectedAt: string | null
  statusSyncedAt: string | null
  status: 'archived' | 'disconnected' | 'active' | 'restricted' | 'onboarding'
  usable: boolean
  unusableReason: string | null
  onboardingUrl: string | null
  organisations: { id: number; name: string }[]
}

export const PAYMENT_ACCOUNT_FIELDS = `
  id
  displayName
  stripeAccountId
  livemode
  country
  defaultCurrency
  contactEmail
  chargesEnabled
  payoutsEnabled
  detailsSubmitted
  disabledReason
  isActive
  disconnectedAt
  statusSyncedAt
  status
  usable
  unusableReason
  onboardingUrl
  organisations {
    id
    name
  }
`

const GET_PAYMENT_ACCOUNTS = gql`
  query getPaymentAccounts {
    getPaymentAccounts {
      ${PAYMENT_ACCOUNT_FIELDS}
    }
  }
`

const CREATE_PAYMENT_ACCOUNT = gql`
  mutation createPaymentAccount($input: CreatePaymentAccountInput!) {
    createPaymentAccount(input: $input) {
      ${PAYMENT_ACCOUNT_FIELDS}
    }
  }
`

const REFRESH_PAYMENT_ACCOUNT = gql`
  mutation refreshPaymentAccount($id: Int!) {
    refreshPaymentAccount(id: $id) {
      ${PAYMENT_ACCOUNT_FIELDS}
    }
  }
`

const ARCHIVE_PAYMENT_ACCOUNT = gql`
  mutation archivePaymentAccount($id: Int!) {
    archivePaymentAccount(id: $id) {
      ${PAYMENT_ACCOUNT_FIELDS}
    }
  }
`

const ROTATE_ONBOARDING_LINK = gql`
  mutation rotatePaymentAccountOnboardingLink($id: Int!) {
    rotatePaymentAccountOnboardingLink(id: $id) {
      ${PAYMENT_ACCOUNT_FIELDS}
    }
  }
`

const STATUS_LABEL: Record<PaymentAccount['status'], string> = {
  active: 'Active',
  restricted: 'Restricted',
  onboarding: 'Onboarding incomplete',
  disconnected: 'Disconnected',
  archived: 'Archived',
}

const STATUS_CLASS: Record<PaymentAccount['status'], string> = {
  active: 'bg-green-100 text-green-800',
  restricted: 'bg-amber-100 text-amber-800',
  onboarding: 'bg-blue-100 text-blue-800',
  disconnected: 'bg-red-100 text-red-800',
  archived: 'bg-gray-200 text-gray-600',
}

const errorMessage = (error: any, fallback: string): string => error?.response?.errors?.[0]?.message || fallback

const PaymentAccountsView: React.FC = () => {
  const [accounts, setAccounts] = useState<PaymentAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [copiedId, setCopiedId] = useState<number | null>(null)
  // The toast helper in this app is not wired to anything that renders, so every
  // message here is an inline banner, as the organisation Settings tab does it.
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [displayName, setDisplayName] = useState('')
  const [country, setCountry] = useState('FR')
  const [contactEmail, setContactEmail] = useState('')
  const [creating, setCreating] = useState(false)

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true)
      const response = await authorizedGraphQLRequest<{ getPaymentAccounts: PaymentAccount[] }>(GET_PAYMENT_ACCOUNTS)
      setAccounts(response.getPaymentAccounts || [])
    } catch (err: any) {
      setError(errorMessage(err, 'Failed to load payment accounts'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  const replaceAccount = (updated: PaymentAccount) => setAccounts((current) => current.map((a) => (a.id === updated.id ? updated : a)))

  const handleCreate = async () => {
    setError(null)
    setSuccess(null)
    try {
      setCreating(true)
      const response = await authorizedGraphQLRequest<{ createPaymentAccount: PaymentAccount }>(CREATE_PAYMENT_ACCOUNT, {
        input: { displayName, country, contactEmail: contactEmail.trim() || null },
      })
      setAccounts((current) => [...current, response.createPaymentAccount])
      setDisplayName('')
      setContactEmail('')
      setShowForm(false)
      setSuccess('Account created on Stripe. Send the partner their onboarding link.')
    } catch (err: any) {
      setError(errorMessage(err, 'Failed to create the payment account'))
    } finally {
      setCreating(false)
    }
  }

  const run = async <T,>(id: number, document: string, key: string, done?: string) => {
    setError(null)
    setSuccess(null)
    try {
      setBusyId(id)
      const response = await authorizedGraphQLRequest<Record<string, PaymentAccount>>(document, { id })
      replaceAccount(response[key] as PaymentAccount)
      if (done) setSuccess(done)
    } catch (err: any) {
      setError(errorMessage(err, 'The request failed'))
    } finally {
      setBusyId(null)
    }
  }

  const handleCopy = async (account: PaymentAccount) => {
    if (!account.onboardingUrl) return
    try {
      await navigator.clipboard.writeText(account.onboardingUrl)
      setCopiedId(account.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      setError('Could not copy the link. Select and copy it by hand.')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="font-semibold text-gray-800">Payment accounts ({accounts.length})</h4>
          <p className="text-xs text-gray-500 mt-1">Each one is a partner's own Stripe account. They are paid directly and handle their own tax.</p>
        </div>
        <Button onClick={() => setShowForm((open) => !open)}>
          <Plus className="h-4 w-4 mr-1" />
          New payment account
        </Button>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 text-sm text-red-700 border border-red-100">{error}</div>}
      {success && <div className="p-3 rounded-lg bg-green-50 text-sm text-green-700 border border-green-100">{success}</div>}

      {showForm && (
        <div className="border border-gray-200 rounded-xl p-4">
          <h5 className="font-semibold text-gray-800 mb-1">New payment account</h5>
          <p className="text-xs text-gray-500 mb-3">
            Creates the account on Stripe. The partner completes identity and bank details themselves, through the link you send them; Georgia never sees them.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-56">
              <label className="block text-xs font-medium text-gray-600 mb-1">Partner name</label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Partner SARL"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Country</label>
              <input
                value={country}
                onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="FR"
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="min-w-56">
              <label className="block text-xs font-medium text-gray-600 mb-1">Partner email (optional)</label>
              <input
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="partner@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <Button onClick={handleCreate} disabled={creating || !displayName.trim() || country.length !== 2}>
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </div>
      )}

      {accounts.length === 0 ? (
        <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
          <CreditCard className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No payment accounts yet</p>
          <p className="text-sm mt-1">Create one per partner, then attach it to their organisation</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {accounts.map((account) => {
            const busy = busyId === account.id
            return (
              <div key={account.id} className={`bg-white border border-gray-200 rounded-xl p-5 shadow-sm ${account.isActive ? '' : 'opacity-70'}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <h5 className="font-semibold text-gray-800 truncate">{account.displayName}</h5>
                    <p className="text-xs text-gray-500 font-mono truncate">{account.stripeAccountId}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* A live account on a test-mode server is shown and never used. */}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${account.livemode ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}>
                      {account.livemode ? 'Live' : 'Test'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_CLASS[account.status]}`}>{STATUS_LABEL[account.status]}</span>
                  </div>
                </div>

                <dl className="text-xs text-gray-600 space-y-1 mb-4">
                  <div className="flex gap-2">
                    <dt className="text-gray-500">Country</dt>
                    <dd>{account.country}</dd>
                    {account.defaultCurrency && <dd className="uppercase">· {account.defaultCurrency}</dd>}
                  </div>
                  {account.contactEmail && (
                    <div className="flex gap-2">
                      <dt className="text-gray-500">Contact</dt>
                      <dd className="truncate">{account.contactEmail}</dd>
                    </div>
                  )}
                  {account.disabledReason && (
                    <div className="flex gap-2">
                      <dt className="text-gray-500">Stripe says</dt>
                      <dd className="text-amber-700">{account.disabledReason}</dd>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <dt className="text-gray-500">Organisations</dt>
                    <dd className="truncate">{account.organisations.length === 0 ? 'None yet' : account.organisations.map((o) => o.name).join(', ')}</dd>
                  </div>
                  {!account.usable && account.unusableReason === 'wrong_mode' && (
                    <div className="text-amber-700">This account belongs to the other Stripe mode, so nothing can be bought on it here.</div>
                  )}
                </dl>

                {account.onboardingUrl && account.isActive && (
                  <div className="flex items-center gap-2 mb-3">
                    <input readOnly value={account.onboardingUrl} className="flex-1 min-w-0 px-2 py-1.5 border border-gray-200 rounded-lg text-xs bg-gray-50 font-mono" />
                    <button onClick={() => handleCopy(account)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700" title="Copy the onboarding link">
                      {copiedId === account.id ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || !account.isActive}
                    onClick={() => run(account.id, REFRESH_PAYMENT_ACCOUNT, 'refreshPaymentAccount', 'Status re-read from Stripe.')}
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    Refresh status
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || !account.isActive}
                    onClick={() => {
                      if (!confirm('Rotate the onboarding link? The one you already sent will stop working.')) return
                      run(account.id, ROTATE_ONBOARDING_LINK, 'rotatePaymentAccountOnboardingLink', 'New link created. The old one no longer works.')
                    }}
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                    Rotate link
                  </Button>
                  {account.isActive && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => {
                        if (!confirm('Archive this account? Its organisations can no longer take payments. Purchases already made are unchanged.')) return
                        run(account.id, ARCHIVE_PAYMENT_ACCOUNT, 'archivePaymentAccount', 'Account archived.')
                      }}
                    >
                      <Archive className="h-3.5 w-3.5 mr-1" />
                      Archive
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default PaymentAccountsView
